const seoRoles = [
  {
    slug: "software-engineer",
    title: "Software Engineer Cover Letter Example & Tips",
    description:
      "Write a focused software engineer cover letter with a realistic example, key skills, practical tips, and answers to common questions.",
    skills: [
      "System design",
      "Programming languages",
      "Testing",
      "Code review",
      "Cloud platforms",
      "Cross-functional collaboration",
    ],
    intro:
      "A strong software engineer cover letter connects technical decisions to outcomes. Instead of listing every language you know, show how you used the most relevant tools to improve reliability, delivery speed, customer experience, or engineering quality.",
    tips: [
      "Lead with an engineering result that matches the role's main responsibility.",
      "Name the relevant language, platform, or architecture only when it supports a concrete example.",
      "Explain one tradeoff you handled, such as speed versus maintainability or cost versus scale.",
      "Connect your experience to the company's product and current engineering challenges.",
    ],
    exampleCoverLetter: `Dear Hiring Team,

I am applying for the Software Engineer role because your focus on making complex workflows easier for customers closely matches the work I enjoy. In my current role, I redesigned a high-traffic service that reduced median response time by 35% while making failures easier for the team to diagnose.

I work primarily with JavaScript, Python, PostgreSQL, and AWS, but the result matters more to me than the stack. I collaborate with product and design partners early, write tests around critical behavior, and document tradeoffs so that the next engineer can build confidently. Most recently, that approach helped our team ship a billing migration without customer downtime.

I would welcome the opportunity to bring that combination of product judgment and dependable engineering to your team. Thank you for considering my application.

Sincerely,
Alex Morgan`,
    faq: [
      {
        question: "How technical should a software engineer cover letter be?",
        answer:
          "Use enough technical detail to establish credibility, but focus on decisions and outcomes. One clear project example is more useful than a long list of tools.",
      },
      {
        question: "Should I repeat projects from my resume?",
        answer:
          "Reference one relevant project, then add context your resume cannot show, such as the problem, tradeoff, collaboration, or customer impact.",
      },
    ],
  },
  {
    slug: "frontend-developer",
    title: "Frontend Developer Cover Letter Example & Tips",
    description:
      "Create a frontend developer cover letter that highlights accessible interfaces, performance, collaboration, and measurable product outcomes.",
    skills: [
      "JavaScript",
      "React",
      "Responsive design",
      "Web accessibility",
      "Performance optimization",
      "Design systems",
    ],
    intro:
      "Frontend hiring teams want evidence that you can turn product and design intent into a fast, accessible experience. Your letter should pair interface craftsmanship with a specific user or business outcome rather than concentrating only on frameworks.",
    tips: [
      "Describe a visible user problem you solved and how you measured improvement.",
      "Mention accessibility, performance, or browser compatibility when the job emphasizes quality.",
      "Show how you worked with designers and backend engineers to resolve constraints.",
      "Link your interest to a specific part of the employer's product experience.",
    ],
    exampleCoverLetter: `Dear Hiring Team,

The Frontend Developer opening stood out to me because your product turns a complicated process into an interface that feels calm and direct. That is the kind of frontend problem I like solving. At my current company, I rebuilt the account setup flow in React and reduced abandonment by 22% while meeting WCAG keyboard and screen-reader requirements.

My work spans component architecture, responsive CSS, API integration, testing, and performance. I partner closely with designers before implementation and use real browser data after launch to find what still needs attention. On our design-system rollout, this approach reduced duplicated UI code and helped three product teams ship consistent features faster.

I would be excited to help make your customer experience faster, clearer, and more inclusive. Thank you for your time.

Sincerely,
Jordan Lee`,
    faq: [
      {
        question: "Should a frontend cover letter mention a portfolio?",
        answer:
          "Yes. Link one focused portfolio or live project and briefly explain the user problem, your contribution, and the result.",
      },
      {
        question: "Do I need to list every frontend framework?",
        answer:
          "No. Prioritize the stack in the job description and demonstrate that you understand reusable UI, accessibility, testing, and performance beyond one framework.",
      },
    ],
  },
  {
    slug: "backend-developer",
    title: "Backend Developer Cover Letter Example & Tips",
    description:
      "Write a backend developer cover letter focused on reliable APIs, databases, security, scale, and business impact with a practical example.",
    skills: [
      "API design",
      "Databases",
      "Distributed systems",
      "Authentication",
      "Observability",
      "Performance tuning",
    ],
    intro:
      "A backend developer cover letter should make invisible work tangible. Use one example to show how an API, data model, queue, or reliability improvement changed system behavior for customers or other engineers.",
    tips: [
      "Quantify latency, uptime, throughput, cost, or developer-time improvements where accurate.",
      "Explain how you protected data or designed for failure instead of saying only that an API was scalable.",
      "Show that you can communicate backend constraints to product and frontend partners.",
      "Match your strongest example to the employer's architecture or product stage.",
    ],
    exampleCoverLetter: `Dear Hiring Team,

I am interested in the Backend Developer role because your team is building infrastructure that customers need to trust every day. In my current position, I replaced a synchronous order workflow with an idempotent queue-based service, reducing timeout-related failures by 68% during peak traffic.

I build Node.js and Python APIs backed by PostgreSQL and Redis, with attention to authentication, useful observability, and safe migrations. I also enjoy making backend systems easier for other engineers to use. A recent API redesign cut the integration time for our frontend team from several days to one afternoon and gave support clearer error information.

I would value the chance to help your team build services that remain understandable and dependable as the product grows.

Sincerely,
Taylor Kim`,
    faq: [
      {
        question: "What metrics work well in a backend developer cover letter?",
        answer:
          "Use truthful measures such as latency, error rate, uptime, throughput, cloud cost, deployment frequency, or integration time.",
      },
      {
        question: "Should I describe backend architecture in detail?",
        answer:
          "Describe only the parts needed to understand your decision and its impact. Save a full architectural walkthrough for the interview.",
      },
    ],
  },
  {
    slug: "full-stack-developer",
    title: "Full-Stack Developer Cover Letter Example & Tips",
    description:
      "Build a full-stack developer cover letter that shows end-to-end ownership, product judgment, technical breadth, and measurable results.",
    skills: [
      "React",
      "API development",
      "SQL databases",
      "Testing",
      "Cloud deployment",
      "Product collaboration",
    ],
    intro:
      "For a full-stack role, breadth is useful only when it supports ownership. Show how you moved a feature from customer need through interface, API, data, deployment, and iteration without turning the letter into a stack inventory.",
    tips: [
      "Choose one end-to-end feature and clarify which parts you personally owned.",
      "Balance frontend experience with backend reliability and data considerations.",
      "Demonstrate product judgment, not just the ability to work in multiple folders.",
      "Tailor the technical emphasis to the layer where the employer needs the most help.",
    ],
    exampleCoverLetter: `Dear Hiring Team,

Your Full-Stack Developer role appeals to me because the team owns customer problems from first sketch through production. I recently led a self-service reporting feature from discovery to launch, building the React interface, Node API, PostgreSQL queries, and deployment monitoring. Within two months, it was used by 41% of active accounts and reduced manual report requests to support.

I am comfortable moving across the stack, but I do not treat every layer the same. I work with designers to simplify interactions, review data access for correctness, add tests where failures would affect customers, and monitor behavior after release. That end-to-end view helps me spot product and operational issues before they become expensive.

I would be glad to bring that ownership and practical engineering approach to your product team.

Sincerely,
Casey Rivera`,
    faq: [
      {
        question: "How do I prove I am truly full stack?",
        answer:
          "Describe one feature you delivered across multiple layers and specify your decisions in the interface, API, data model, and release process.",
      },
      {
        question: "Should I give equal space to frontend and backend work?",
        answer:
          "Not necessarily. Match the role description. Emphasize the area most important to that team while showing you can own the complete path.",
      },
    ],
  },
  {
    slug: "python-developer",
    title: "Python Developer Cover Letter Example & Tips",
    description:
      "Write a Python developer cover letter with relevant framework, automation, data, testing, and performance examples tailored to the role.",
    skills: [
      "Python",
      "Django or FastAPI",
      "Automated testing",
      "SQL",
      "Data processing",
      "API integration",
    ],
    intro:
      "Python roles vary widely across web services, automation, data, and machine learning. A useful cover letter quickly identifies the kind of Python work you have done and connects it to the employer's actual use case.",
    tips: [
      "Name the relevant Python domain early: web APIs, automation, data pipelines, or tooling.",
      "Use an example that demonstrates readable, tested code rather than relying on language enthusiasm.",
      "Mention a framework only when it matches the role or supports a meaningful result.",
      "Show how your Python work saved time, improved reliability, or enabled a product capability.",
    ],
    exampleCoverLetter: `Dear Hiring Team,

I am applying for the Python Developer position because your team is using automation to remove repetitive operational work. At my current company, I built a Python service that validates and reconciles partner data before import, cutting a four-hour daily process to under twenty minutes and reducing avoidable data errors.

My recent work uses FastAPI, PostgreSQL, pytest, and background workers. I care about clear interfaces and operational behavior as much as implementation speed, so I add structured logging, useful failure messages, and tests around external integrations. That made the reconciliation service straightforward for our operations team to monitor without engineering help.

I would enjoy applying that practical approach to the automation and data challenges your team is tackling.

Sincerely,
Morgan Patel`,
    faq: [
      {
        question: "Should I include personal Python projects?",
        answer:
          "Include a project when it demonstrates skills relevant to the role, especially if you are early in your career. Explain the problem and result, not only the repository link.",
      },
      {
        question: "How should I tailor a Python cover letter?",
        answer:
          "Identify whether the job centers on web development, automation, data, or machine learning, then select evidence from the same domain.",
      },
    ],
  },
  {
    slug: "react-developer",
    title: "React Developer Cover Letter Example & Tips",
    description:
      "Create a React developer cover letter highlighting component design, state management, performance, testing, accessibility, and user outcomes.",
    skills: [
      "React",
      "JavaScript",
      "Component architecture",
      "State management",
      "Testing Library",
      "Accessibility",
    ],
    intro:
      "A React developer cover letter should show that you can build maintainable product interfaces, not simply that you know hooks. Highlight component decisions, quality practices, and the user impact of the experience you shipped.",
    tips: [
      "Describe how you structured reusable components without making simple screens abstract.",
      "Include a performance, accessibility, or testing improvement tied to users or delivery speed.",
      "Explain how you handled state and API behavior in one complex interaction.",
      "Reference the employer's product interface rather than praising React itself.",
    ],
    exampleCoverLetter: `Dear Hiring Team,

I am drawn to the React Developer role because your team is improving a product people rely on throughout their workday. In my current role, I rebuilt a complex scheduling interface into smaller, tested React components and simplified its state transitions. The change reduced UI-related support tickets by 29% and made new scheduling rules faster to add.

I work with modern React, JavaScript, Testing Library, and accessible HTML, but my focus is predictable behavior for users and maintainable choices for the team. I collaborate closely with design on interaction details and with API engineers on loading, error, and empty states that often determine whether a feature feels trustworthy.

I would welcome the opportunity to help your team create responsive, durable interfaces for its next stage of growth.

Sincerely,
Riley Chen`,
    faq: [
      {
        question: "Is React experience enough for a React developer role?",
        answer:
          "Employers usually also expect strong JavaScript, semantic HTML, CSS, API integration, testing, and debugging. Show the complete interface-development skill set.",
      },
      {
        question: "Should I mention specific React hooks?",
        answer:
          "Only when they are relevant to a meaningful design decision. Outcomes and maintainability are stronger evidence than naming individual hooks.",
      },
    ],
  },
  {
    slug: "java-developer",
    title: "Java Developer Cover Letter Example & Tips",
    description:
      "Write a Java developer cover letter focused on dependable services, Spring, testing, performance, integrations, and measurable impact.",
    skills: [
      "Java",
      "Spring Boot",
      "REST APIs",
      "SQL",
      "JUnit",
      "Microservices",
    ],
    intro:
      "Java developer roles often emphasize long-lived systems where correctness and maintainability matter. Your cover letter should demonstrate how you improved a service, integration, or delivery process while working safely within a larger codebase.",
    tips: [
      "Show how you improved a production Java system rather than describing coursework alone.",
      "Mention Spring, messaging, or persistence tools in the context of an architectural decision.",
      "Include testing, monitoring, or migration practices that reduced operational risk.",
      "Connect your experience to the employer's transaction volume, domain, or modernization goals.",
    ],
    exampleCoverLetter: `Dear Hiring Team,

I am interested in the Java Developer role because your team is modernizing services that support high-volume customer transactions. In my current role, I separated a fragile payment workflow into well-defined Spring Boot services, introduced contract tests, and reduced deployment-related incidents by 44% over two quarters.

My experience includes Java, Spring, PostgreSQL, Kafka, and JUnit across systems where data consistency and clear failure handling matter. I work carefully with legacy behavior, document migration decisions, and partner with operations before releases. During our payment change, that discipline allowed us to migrate traffic gradually without interrupting customer checkout.

I would be pleased to bring my experience building dependable Java services to your modernization work.

Sincerely,
Jamie Brooks`,
    faq: [
      {
        question: "What should an experienced Java developer emphasize?",
        answer:
          "Focus on production reliability, architecture, performance, migrations, and collaboration in large codebases rather than basic language knowledge.",
      },
      {
        question: "Can a new graduate write a strong Java cover letter?",
        answer:
          "Yes. Use an internship, substantial project, or open-source contribution to demonstrate testing, data modeling, debugging, and thoughtful design.",
      },
    ],
  },
  {
    slug: "devops-engineer",
    title: "DevOps Engineer Cover Letter Example & Tips",
    description:
      "Create a DevOps engineer cover letter that demonstrates automation, cloud infrastructure, delivery improvements, observability, and reliability.",
    skills: [
      "CI/CD",
      "Cloud infrastructure",
      "Infrastructure as code",
      "Containers",
      "Observability",
      "Incident response",
    ],
    intro:
      "DevOps cover letters are strongest when they quantify safer and faster delivery. Show how your automation or platform work changed lead time, recovery, reliability, cost, or the day-to-day experience of developers.",
    tips: [
      "Use delivery and reliability measures such as deployment time, failure rate, recovery time, or uptime.",
      "Explain how teams adopted your platform or automation, not only how you configured it.",
      "Demonstrate calm incident handling and learning through follow-up improvements.",
      "Match cloud and infrastructure tools to those named in the job description.",
    ],
    exampleCoverLetter: `Dear Hiring Team,

Your DevOps Engineer role caught my attention because the platform team treats developer experience and reliability as the same problem. At my current company, I replaced manual releases with a tested CI/CD workflow and reusable Terraform modules. Deployment time fell from 50 minutes to 12, while failed releases became easier to roll back safely.

I have hands-on experience with AWS, Kubernetes, Terraform, GitHub Actions, and observability tooling. More importantly, I work with application teams to understand where delivery actually gets stuck. After reviewing our incident patterns, I added service-level alerts and runbooks that reduced median recovery time by 38% without increasing alert noise.

I would be excited to help your teams ship confidently while keeping the platform understandable and resilient.

Sincerely,
Avery Singh`,
    faq: [
      {
        question: "Which DevOps metrics should I include?",
        answer:
          "Useful measures include deployment frequency, lead time, change failure rate, recovery time, uptime, cloud cost, and developer onboarding time.",
      },
      {
        question: "How do I avoid making my letter a tools list?",
        answer:
          "Start with an operational or delivery problem, explain what you changed, and name tools only as part of that solution.",
      },
    ],
  },
  {
    slug: "data-scientist",
    title: "Data Scientist Cover Letter Example & Tips",
    description:
      "Write a data scientist cover letter connecting analysis and modeling to decisions, experimentation, communication, and measurable outcomes.",
    skills: [
      "Python",
      "SQL",
      "Statistical modeling",
      "Experimentation",
      "Data visualization",
      "Stakeholder communication",
    ],
    intro:
      "A data scientist cover letter should connect technical work to a decision someone made differently. Highlight the question, analytical approach, validation, and outcome instead of listing algorithms without context.",
    tips: [
      "Frame one project around the business or product question it answered.",
      "Explain how you validated a model or analysis and communicated uncertainty.",
      "Include adoption or decision impact, not only accuracy improvements.",
      "Tailor the balance of modeling, experimentation, and analytics to the team's stated work.",
    ],
    exampleCoverLetter: `Dear Hiring Team,

I am applying for the Data Scientist role because your team uses behavioral data to improve how customers discover the right product. In my current role, I developed a churn-risk model and worked with customer success to turn its output into a weekly intervention workflow. The pilot improved retention in the targeted group by 8% compared with the control.

My work combines Python, SQL, statistical modeling, experimentation, and clear communication with product partners. I document assumptions, test for leakage and bias, and translate uncertainty into choices stakeholders can act on. For the retention project, a simple explanation dashboard was as important to adoption as the model itself.

I would value the chance to apply rigorous, practical data science to the customer questions your team is exploring.

Sincerely,
Sam Williams`,
    faq: [
      {
        question: "Should I include model accuracy in my cover letter?",
        answer:
          "Include it when the metric is meaningful, but pair it with validation context and the decision or outcome the model supported.",
      },
      {
        question: "How is a data scientist cover letter different from a data analyst letter?",
        answer:
          "Data scientist letters often emphasize modeling, experimentation, and production collaboration, while still showing strong analysis and communication.",
      },
    ],
  },
  {
    slug: "machine-learning-engineer",
    title: "Machine Learning Engineer Cover Letter Example & Tips",
    description:
      "Build a machine learning engineer cover letter focused on production models, pipelines, evaluation, monitoring, scale, and product impact.",
    skills: [
      "Machine learning",
      "Python",
      "Model evaluation",
      "Data pipelines",
      "MLOps",
      "Production monitoring",
    ],
    intro:
      "Machine learning engineering sits between modeling and dependable production software. Your cover letter should show that you can evaluate a model honestly, deploy it safely, monitor real behavior, and connect it to a useful product outcome.",
    tips: [
      "Describe the complete path from data and evaluation through deployment and monitoring.",
      "Use an offline and online metric when accurate, and explain why each mattered.",
      "Mention latency, cost, drift, or reliability when the model served production traffic.",
      "Connect your experience to the employer's current ML product rather than discussing AI generally.",
    ],
    exampleCoverLetter: `Dear Hiring Team,

I am interested in the Machine Learning Engineer position because your team is bringing language models into a workflow where latency and trustworthy output both matter. In my current role, I productionized a document-classification system that processes 1.2 million records each month, improving routing accuracy by 14 points while keeping p95 inference latency below the product target.

I work across Python modeling code, data pipelines, evaluation sets, APIs, and production monitoring. I partnered with domain experts to define failure categories before optimizing the model, then added drift and confidence monitoring after launch. That feedback loop helped us catch a source-data change before it affected a large customer batch.

I would be excited to bring this production-minded ML approach to the systems your team is building.

Sincerely,
Drew Martinez`,
    faq: [
      {
        question: "What distinguishes an ML engineer cover letter?",
        answer:
          "It should demonstrate both machine learning judgment and production engineering, including evaluation, deployment, monitoring, and operational tradeoffs.",
      },
      {
        question: "Should I mention research papers or competitions?",
        answer:
          "Include them when relevant, especially early in your career, but explain your implementation, evaluation choices, and what the work demonstrates for this role.",
      },
    ],
  },
];

export const seoRoleSlugs = seoRoles.map((role) => role.slug);

export function getSeoRole(slug) {
  return seoRoles.find((role) => role.slug === slug);
}

export default seoRoles;
